import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import api from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { getRoleCapabilities } from "../../config/permissions";
import {
  openStillInputFormForBrand,
  openVideoInputFormForBrand,
} from "../../utils/layoutFormBridge";

function resolveBrandLogoUrl(logoUrl) {
  if (!logoUrl) return "/logo192.png";
  if (/^(https?:)?\/\//i.test(logoUrl) || logoUrl.startsWith("data:")) return logoUrl;
  const base = (api.defaults.baseURL || "").replace(/\/$/, "");
  if (!base) return logoUrl;
  return `${base}${logoUrl.startsWith("/") ? "" : "/"}${logoUrl}`;
}

export default function Brands() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { user } = useAuth();
  const caps = useMemo(() => getRoleCapabilities(user?.role), [user?.role]);

  const [items, setItems] = useState([]);
  const [postTypeDialog, setPostTypeDialog] = useState({ open: false, brand: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/brands");
        setItems(data || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const openPostTypePicker = (brand) => {
    setPostTypeDialog({ open: true, brand });
  };

  const closePostTypePicker = () => {
    setPostTypeDialog({ open: false, brand: null });
  };

  const chooseStillPost = () => {
    if (postTypeDialog.brand) openStillInputFormForBrand(postTypeDialog.brand);
    closePostTypePicker();
  };

  const chooseVideoPost = () => {
    if (postTypeDialog.brand) openVideoInputFormForBrand(postTypeDialog.brand);
    closePostTypePicker();
  };

  return (
    <Box m="20px">
      <Header
        title="BRANDS"
        subtitle={caps.isDesigner ? "Brands assigned to you — open layouts or the editor" : "Brand library and layout entry points"}
      />
      <Grid container spacing={3} mt={1}>
        {items.map((b) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={b.id}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardMedia
                component="img"
                height="140"
                image={resolveBrandLogoUrl(b.logoUrl)}
                alt=""
                sx={{ objectFit: "contain", bgcolor: colors.primary[500], p: 1 }}
              />
              <CardContent>
                <Typography variant="h6" noWrap title={b.brandName}>
                  {b.brandName || "Untitled brand"}
                </Typography>
              </CardContent>
              <CardActions sx={{ flexDirection: "column", alignItems: "stretch", gap: 1, p: 2 }}>
                {caps.isAdmin && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(`/form/${b.id}`)}
                  >
                    Edit client profile
                  </Button>
                )}
                {caps.canManageLayouts && (
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    onClick={() => openPostTypePicker(b)}
                  >
                    Create layout
                  </Button>
                )}
                {caps.canManageLayouts && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={{ color: colors.greenAccent[400], borderColor: colors.greenAccent[400] }}
                    onClick={() => navigate(`/brands/${b.id}/layouts`)}
                  >
                    Existing layouts
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      {items.length === 0 ? (
        <Typography mt={2} color={colors.grey[300]}>
          No brands yet. An admin or manager can create brands from the Client page.
        </Typography>
      ) : null}

      <Dialog open={postTypeDialog.open} onClose={closePostTypePicker} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Choose post type</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {postTypeDialog.brand?.brandName
              ? `Create a layout for ${postTypeDialog.brand.brandName}`
              : "Select how you want to build this layout"}
          </Typography>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<ImageOutlinedIcon />}
              onClick={chooseStillPost}
              sx={{ justifyContent: "flex-start", py: 1.5, px: 2 }}
            >
              Still Post
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<VideocamOutlinedIcon />}
              onClick={chooseVideoPost}
              sx={{ justifyContent: "flex-start", py: 1.5, px: 2 }}
            >
              Video Post
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePostTypePicker}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
